import EmblaCarousel from 'embla-carousel';

const AUTOADVANCE_INTERVAL = 3000;
const MOBILE_BREAKPOINT = '(max-width: 991px)';

const mapIndex = (from, fromCount, toCount) =>
  fromCount <= 1 || toCount <= 1 ? 0 : Math.round((from / (fromCount - 1)) * (toCount - 1));

export default (node) => {
  const viewportNode = node.querySelector('.embla__viewport');
  if (!viewportNode) return;

  const prevBtn = node.querySelector('.product-thumb-carousel__prev');
  const nextBtn = node.querySelector('.product-thumb-carousel__next');
  const productThumb = node.closest('.product-thumb');
  const dotsContainers = productThumb ? Array.from(productThumb.querySelectorAll('.product-thumb-carousel__dots')) : [];
  const productTitle = node.dataset.productTitle || '';

  let emblaApi = null;
  let autoAdvanceTimer = null;
  let initialized = false;
  let lastMethod = 'swipe';
  let isAutoAdvance = false;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const trackNavigate = (method) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'plp_image_navigate',
      item_name: productTitle,
      method,
    });
  };

  const setupLazyLoading = () => {
    const slides = emblaApi.slideNodes();
    const inView = new Set(emblaApi.slidesInView());

    slides.forEach((slide, index) => {
      if (inView.has(index)) return;
      slide.querySelectorAll('img[src]').forEach((img) => {
        img.dataset.lazySrc = img.src;
        img.removeAttribute('src');
      });
      slide.querySelectorAll('video[src], video source[src]').forEach((el) => {
        el.dataset.lazySrc = el.src;
        el.removeAttribute('src');
      });
    });

    const loadInViewMedia = () => {
      emblaApi.slidesInView().forEach((index) => {
        const slide = slides[index];
        slide.querySelectorAll('[data-lazy-src]').forEach((el) => {
          el.src = el.dataset.lazySrc;
          el.removeAttribute('data-lazy-src');
        });
      });
    };

    emblaApi.on('slidesInView', loadInViewMedia);
  };

  const setupDots = () => {
    if (!dotsContainers.length) return;
    const slideCount = emblaApi.scrollSnapList().length;

    const allDotGroups = dotsContainers.map((container) => {
      const dots = Array.from(container.querySelectorAll('.product-thumb-carousel__dot'));
      dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const targetIndex = dots.length === slideCount ? i : mapIndex(i, dots.length, slideCount);
          emblaApi.scrollTo(targetIndex);
          lastMethod = 'arrow';
        });
      });
      return dots;
    });

    const updateDots = () => {
      const selected = emblaApi.selectedScrollSnap();
      allDotGroups.forEach((dots) => {
        const activeDot = dots.length === slideCount
          ? selected
          : mapIndex(selected, slideCount, dots.length);
        dots.forEach((dot, i) => {
          const active = i === activeDot;
          dot.classList.toggle('product-thumb-carousel__dot--active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      });
    };

    emblaApi.on('select', updateDots);
    emblaApi.on('init', updateDots);
    updateDots();
  };

  // Keep only in-view slide links in the tab order / accessibility tree (WCAG 2.4.3 / 4.1.2)
  const setupSlideLinks = () => {
    const slides = emblaApi.slideNodes();

    const updateSlideLinks = () => {
      const inView = new Set(emblaApi.slidesInView());
      slides.forEach((slide, index) => {
        const link = slide.querySelector('a');
        if (!link) return;
        if (inView.has(index)) {
          link.removeAttribute('tabindex');
          link.removeAttribute('aria-hidden');
        } else {
          link.setAttribute('tabindex', '-1');
          link.setAttribute('aria-hidden', 'true');
        }
      });
    };

    emblaApi.on('slidesInView', updateSlideLinks);
    emblaApi.on('select', updateSlideLinks);
    updateSlideLinks();
  };

  const setupArrows = () => {
    if (!prevBtn || !nextBtn) return;

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lastMethod = 'arrow';
      emblaApi.scrollPrev();
      stopAutoAdvance();
      startAutoAdvance();
    });

    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lastMethod = 'arrow';
      emblaApi.scrollNext();
      stopAutoAdvance();
      startAutoAdvance();
    });
  };

  const startAutoAdvance = () => {
    stopAutoAdvance();
    if (prefersReducedMotion.matches) return;
    autoAdvanceTimer = setInterval(() => {
      if (!emblaApi || !node.isConnected) {
        stopAutoAdvance();
        return;
      }
      isAutoAdvance = true;
      emblaApi.scrollNext();
    }, AUTOADVANCE_INTERVAL);
  };

  const stopAutoAdvance = () => {
    if (autoAdvanceTimer) {
      clearInterval(autoAdvanceTimer);
      autoAdvanceTimer = null;
    }
  };

  const initEmbla = () => {
    if (initialized) return;
    initialized = true;

    try {
      emblaApi = EmblaCarousel(viewportNode, {
        loop: true,
        align: 'start',
      });
    } catch (err) {
      console.error('Failed to initialize product carousel:', err);
      initialized = false;
      return;
    }

    setupLazyLoading();
    setupDots();
    setupArrows();
    setupSlideLinks();

    emblaApi.on('select', () => {
      if (isAutoAdvance) {
        isAutoAdvance = false;
        return;
      }
      trackNavigate(lastMethod);
      lastMethod = 'swipe';
    });
  };

  const onMouseEnter = () => {
    initEmbla();
    if (!emblaApi) return;
    isAutoAdvance = true;
    emblaApi.scrollNext(true);
    startAutoAdvance();
  };

  const onMouseLeave = () => {
    stopAutoAdvance();
    if (emblaApi) {
      isAutoAdvance = true;
      emblaApi.scrollTo(0, true);
    }
  };

  // Keyboard users: initialise the carousel and halt auto-advance on focus (WCAG 2.1.1 / 2.2.2)
  const onFocusIn = () => {
    initEmbla();
    stopAutoAdvance();
  };

  let mobileObserver = null;

  const setupMobileInit = () => {
    mobileObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        initEmbla();
        mobileObserver.disconnect();
        mobileObserver = null;
      },
      { threshold: 0.1 },
    );
    mobileObserver.observe(node);
  };

  let currentMode = null;
  const applyMode = (mobile) => {
    const newMode = mobile ? 'mobile' : 'desktop';
    if (newMode === currentMode) return;
    if (currentMode === 'desktop') {
      node.removeEventListener('mouseenter', onMouseEnter);
      node.removeEventListener('mouseleave', onMouseLeave);
      stopAutoAdvance();
    }
    if (currentMode === 'mobile' && mobileObserver) {
      mobileObserver.disconnect();
      mobileObserver = null;
    }
    currentMode = newMode;
    if (mobile) {
      setupMobileInit();
    } else {
      node.addEventListener('mouseenter', onMouseEnter);
      node.addEventListener('mouseleave', onMouseLeave);
    }
  };

  const destroy = () => {
    stopAutoAdvance();
    if (mobileObserver) {
      mobileObserver.disconnect();
      mobileObserver = null;
    }
    node.removeEventListener('mouseenter', onMouseEnter);
    node.removeEventListener('mouseleave', onMouseLeave);
    node.removeEventListener('focusin', onFocusIn);
    if (emblaApi) {
      emblaApi.destroy();
      emblaApi = null;
    }
  };

  node.addEventListener('focusin', onFocusIn);

  const mql = window.matchMedia(MOBILE_BREAKPOINT);
  const onBreakpointChange = (e) => {
    if (!node.isConnected) {
      mql.removeEventListener('change', onBreakpointChange);
      destroy();
      return;
    }
    applyMode(e.matches);
  };

  applyMode(mql.matches);
  mql.addEventListener('change', onBreakpointChange);
};
